import React from 'react';
import IconWrapper from '../../../IconWrapper';
import { FiInfo } from 'react-icons/fi';
import { useDatasets } from '../../../../hooks/useDatasets';

export type QuickFilter = { key: string; name: string; dimension: string };

type Props = {
  quickFilters: QuickFilter[];
  onRemove: (key: string) => void;
  onClearAll: () => void;
  debugUrl: string;
  debugTooltip: string;
  datasetIds?: string[];
  onDatasetClear?: () => void;
};

const QuickFiltersBar: React.FC<Props> = ({ quickFilters, onRemove, onClearAll, debugUrl, debugTooltip, datasetIds = [], onDatasetClear }) => {
  // Fetch real datasets from API
  const { data: datasetsResponse } = useDatasets();
  const datasets = datasetsResponse?.datasets || [];

  const selectedDatasets = datasetIds
    .map(id => datasets.find(d => d.id === id))
    .filter((d): d is NonNullable<typeof d> => d !== undefined);

  const hasFilters = quickFilters.length > 0 || datasetIds.length > 0;

  if (!hasFilters) return null;
  return (
    <div className="px-6 -mt-2 mb-2 flex items-center justify-between">
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-xs text-gray-600 mr-1">Viewed by:</span>
        {selectedDatasets.map((dataset) => (
          <span key={dataset.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs bg-blue-50 border-blue-200">
            <span className="text-blue-700">{dataset.name}</span>
            <button
              className="text-blue-500 hover:text-blue-700"
              aria-label={`Remove ${dataset.name} Collection filter`}
              onClick={() => onDatasetClear?.()}
            >
              ✕
            </button>
          </span>
        ))}
        {quickFilters.map((f) => (
          <span key={f.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs bg-white">
            <span className="text-gray-700">{f.name}</span>
            <button className="text-gray-500 hover:text-gray-700" aria-label={`Remove ${f.name}`} onClick={() => onRemove(f.key)}>
              ✕
            </button>
          </span>
        ))}
        <button
          className="ml-2 text-xs text-go-primary hover:underline"
          onClick={onClearAll}
        >
          Clear all
        </button>
      </div>
      <div>
        <a
          href={debugUrl}
          target="_blank"
          rel="noreferrer"
          title={debugTooltip}
          aria-label={debugTooltip}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-white"
        >
          <IconWrapper icon={FiInfo} className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
};

export default React.memo(QuickFiltersBar);
